import { useRef, useMemo, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text, MeshTransmissionMaterial } from '@react-three/drei';
import * as THREE from 'three';

interface RingProps {
  text: string;
  radius?: number;
}

interface RingInternalProps extends RingProps {
  isHovered: boolean;
}

function Ring({ text = "RING", radius = 3, isHovered }: RingInternalProps) {
  const groupRef = useRef<THREE.Group>(null);

  // Calculate positions for characters on 2 perpendicular circles
  const textElements = useMemo(() => {
    const textRadius = radius - 1.5; // Position text INSIDE the sphere
    const elements: Array<{ char: string; position: [number, number, number]; rotation: [number, number, number] }> = [];

    const chars = text.split('').reverse();
    const angleStep = (Math.PI * 2) / chars.length;

    // Find the index of the bullet symbol to use as reference point
    const bulletIndex = chars.findIndex(c => c === '•');
    const startAngleOffset = bulletIndex !== -1 ? -angleStep * bulletIndex : 0;

    // Ring 1: Horizontal circle (XZ plane)
    // Start with bullet at angle 0 (pointing right along +X axis)
    chars.forEach((char, i) => {
      const angle = angleStep * i + startAngleOffset;
      const x = Math.cos(angle) * textRadius;
      const y = 0; // Stays at y=0 (horizontal)
      const z = Math.sin(angle) * textRadius;

      elements.push({
        char,
        position: [x, y, z],
        rotation: [0, angle + Math.PI / 2, 0],
      });
    });

    // Ring 2: Vertical circle (XY plane) - perpendicular to Ring 1
    // Also start with bullet at angle 0 (same +X position)
    chars.forEach((char, i) => {
      const angle = angleStep * i + startAngleOffset;
      const x = Math.cos(angle) * textRadius; // Same X movement
      const y = Math.sin(angle) * textRadius; // Y moves instead of Z
      const z = 0; // Stays at z=0 (vertical)

      elements.push({
        char,
        position: [x, y, z],
        rotation: [Math.PI / 2, angle + Math.PI / 2, 0], // Rotated 90° to face outward
      });
    });

    return elements;
  }, [text, radius]);

  // Animate rotation - speed up on hover
  useFrame(() => {
    if (groupRef.current) {
      const speedMultiplier = isHovered ? 3 : 1;
      groupRef.current.rotation.y += 0.005 * speedMultiplier; // Main rotation
      groupRef.current.rotation.x += 0.001 * speedMultiplier; // Slight tilt
      groupRef.current.rotation.z += 0.0005 * speedMultiplier; // Very slight wobble
    }
  });

  return (
    <group ref={groupRef}>
      {/* Glass sphere for distortion */}
      <mesh>
        <sphereGeometry args={[radius, 64, 64]} />
        <MeshTransmissionMaterial
          backside={true}
          backsideThickness={5}
          thickness={2}
          transmission={1}
          roughness={0.1}
          metalness={0}
          ior={1.3}
          chromaticAberration={0.13}
          anisotropy={0.5}
          distortion={0.3}
          distortionScale={0.3}
          temporalDistortion={0.05}
          clearcoat={1}
          clearcoatRoughness={0}
        />
      </mesh>

      {/* Text characters positioned around the sphere */}
      {textElements.map((element, i) => (
        <Text
          key={i}
          position={element.position}
          rotation={element.rotation}
          fontSize={0.5}
          color="white"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.02}
          outlineColor="#ffffff"
        >
          {element.char}
        </Text>
      ))}
    </group>
  );
}

interface TextRing3DProps {
  text?: string;
  className?: string;
}

export default function TextRing3D({ text = "• E Q U A L - I Q  • E Q U A L - I Q ", className = "" }: TextRing3DProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={className}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <div
        style={{
          width: '100%',
          aspectRatio: '1',
          maxWidth: '100%',
          maxHeight: '100%',
          borderRadius: '50%',
          overflow: 'visible',
          position: 'relative',
          background: 'radial-gradient(circle at center, transparent 0%, transparent 40%, rgba(0,0,0,0.6) 80%, rgba(0,0,0,0.9) 100%)',
          pointerEvents: 'none'
        }}
      >
        {/* Canvas layer - full size */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            transform: isHovered ? 'scale(1.15)' : 'scale(1)',
            transition: 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
            filter: isHovered ? 'drop-shadow(0 0 40px rgba(255, 255, 255, 0.4))' : 'none',
            pointerEvents: 'none'
          }}
        >
          <Canvas
            camera={{ position: [0, 0, 10], fov: 50 }}
            style={{ background: 'transparent' }}
            gl={{ alpha: true }}
          >
            <ambientLight intensity={isHovered ? 1.5 : 1} />
            <pointLight position={[10, 10, 10]} intensity={isHovered ? 3 : 2} />
            <pointLight position={[-10, -10, -10]} intensity={isHovered ? 1.5 : 1} />
            <Ring text={text} isHovered={isHovered} />
          </Canvas>
        </div>

        {/* Hover detection area - smaller circle */}
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onTouchStart={() => setIsHovered(true)}
          onTouchEnd={() => setIsHovered(false)}
          onTouchCancel={() => setIsHovered(false)}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '50%',
            height: '50%',
            transform: 'translate(-50%, -50%)',
            cursor: 'pointer',
            pointerEvents: 'auto',
            clipPath: 'circle(50% at center)',
            // Debug: uncomment to see hover area
            // background: 'rgba(255, 0, 0, 0.2)'
          }}
        />
      </div>
    </div>
  );
}
